import {
	json,
	type LoaderFunctionArgs,
	type HeadersFunction,
	type LinksFunction,
	type MetaFunction,
} from '@remix-run/node'
import {
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useMatches,
} from '@remix-run/react'
import { withSentry } from '@sentry/remix'
import { SVGProps, useState } from 'react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { buttonVariants } from './components/ui/button.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import {
	ThemeSwitch,
	useOptionalTheme,
	useTheme,
} from './routes/resources+/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import fontStylesheet from './styles/font-styles.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { cn, combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import logo from '#app/assets/gdr-logo-main.png'
import { Dialog, DialogPanel } from '@headlessui/react'
import { Bars3Icon } from '@heroicons/react/24/outline'
import { AnimatePresence, motion, useCycle } from 'framer-motion'
import { GDRLogo } from './components/gdr-logo.tsx'
import { HashNavLink } from './components/ui/hash-link.tsx'
import { Script } from './components/script.tsx'

const navigation = [
	{ name: 'About Us', href: '#About' },
	{ name: 'Services', href: '#Services' },
	{ name: 'Reviews', href: '#Reviews' },
	{ name: 'Service Areas', href: '#ServiceAreas' },
]

interface SocialLink {
	name: string;
	href: string;
	icon: (props: SVGProps<SVGSVGElement>) => JSX.Element;
}

const social: SocialLink[] = [
    {
      name: 'Facebook',
      href: 'https://www.facebook.com/garagedoorrepairllc/',
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    {
      name: 'Instagram',
      href: 'https://www.instagram.com/garagedoorrepairllc/',
      icon: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    {
      name: 'LinkedIn',
      href: 'https://www.linkedin.com/in/garage-door-repair-llc/',
      icon: (props) => (
		<svg viewBox="0 0 24 24" fill="currentColor" {...props}>
			<path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"></path>
		</svg>
      ),
    }
]
	


export const links: LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
		{ rel: 'preload', href: fontStylesheet, as: 'style' },
		{ rel: 'stylesheet', href: fontStylesheet },
	].filter(Boolean)
}

export const meta: MetaFunction = () => {
	return [
		{ title: 'Garage Door Repair of Dayton, OH' },
		{ name: 'description', content: `Your own captain's log` },
	]
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { id: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = honeypot.getInputProps()

	return json(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
			googleCaptchaSiteKey: process.env.GOOGLE_RECAPTCHA_SITE_KEY,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

export type RootLoader = typeof loader;

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	const data = useLoaderData<typeof loader | null>()
	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
				<Script
				nonce={nonce} 
				src={`https://www.google.com/recaptcha/api.js?render=${data?.googleCaptchaSiteKey}`}
				type='text/javascript'
				waitForHydration
				/>
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const theme = useTheme()
	const matches = useMatches()
	useToast(data.toast)

	return (
		<>
			<div className="flex h-screen flex-col justify-between">
				<Header theme={data?.requestInfo.userPrefs.theme} />

				<div className="flex-1">
					<Outlet />
				</div>

				<Footer />
			</div>
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</>
	)
}

function Logo() {
	return (
		<Link to="/" className="group grid max-w-max leading-snug py-3">
			<span className="sr-only">Garage Door Repair LLC</span>
			<svg className='w-24' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
				<circle cx="256" cy="256" r="256" fill='#ffe100' stroke='none'/>
				<circle cx="256" cy="256" r="238.25" fill="none" stroke="#000" strokeWidth="15px" />
				<polygon points="327.64 175.23 339.9 175.23 289.44 87.29 259.45 87.29 240.99 55.07 200.16 55.07 131.57 175.23 172.3 175.23 234.9 65.81 247.93 88.52 198.26 175.23 239.29 175.23 283.42 97.95 327.64 175.23"/>
				<rect x="266.17" y="140.66" width="15.1" height="15.1" rx="7.55" ry="7.55"/>
				<rect x="285.75" y="140.66" width="15.1" height="15.1" rx="7.55" ry="7.55"/>
				<rect x="266.17" y="160.13" width="15.1" height="15.1" rx="7.55" ry="7.55"/>
				<rect x="285.75" y="160.13" width="15.1" height="15.1" rx="7.55" ry="7.55"/>
				<path d="M130.99,268.21v31.82l21.71-.13v20.56s-6.46,5.55-24.06,1.15c0,0-11.15-2.35-17.31-10.56,0,0-5.87-6.16-7.92-21.71,0,0-2.35-12.03,1.47-25.82,0,0,6.75-26.41,32.28-26.11,0,0,12.32-2.05,26.99,10.56l20.54-24.94s-21.71-25.53-62.79-18.19c0,0-17.31,1.76-33.45,15.84,0,0-12.32,7.92-21.71,32.86,0,0-11.15,42.84,6.46,71.01,0,0,18.19,32.57,63.67,31.98,0,0,29.63,2.05,50.47-18.19v-69.79l-56.33-.34Z"/>
				<rect x="197" y="206.14" width="38.71" height="147.63"/>
				<path d="M236.32,236.72s25.23-.59,34.04,12.7c0,0,7.04,8.27,6.75,29.54,0,0,1.47,25.99-12.32,36.33,0,0-6.75,6.88-28.46,7.58h-38.41v31.02h53.67s39.32-.79,56.04-31.51c0,0,12.57-20.8,9.07-48.8,0,0,1.76-39.08-27.13-55.54,0,0-13.63-11.39-37.98-12.01h-53.67l-.02,30.99,38.44-.3Z"/>
				<rect x="322.9" y="206.16" width="38.71" height="147.63"/>
				<path d="M362.49,303.71h15.43s17.73-.59,36.8-7.92c0,0,18.19-8.51,21.71-26.11,0,0,7.04-28.17-5.28-44.3,0,0-12.82-19.32-46.02-19.34h-61.39l.02,30.49,57.33.12s17.49.17,18.96,12.49c0,0,2.64,13.2-5.57,19.07s-13.39,4.72-13.39,4.72l-18.3-.03-39.05.52v30.28h38.75Z"/>
				<polygon points="416.18 296.38 448.75 353.88 405.17 353.88 379.8 304 370.12 288.75 408.26 279.94 416.18 296.38"/>
			</svg>
		</Link>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default withSentry(AppWithProviders)

const MotionLink = motion.create(HashNavLink);

const mobile = {
	open: (height = 1000) => ({
	  clipPath: `circle(${height * 2 + 200}px at 40px 40px)`,
	  transition: {
		type: "spring",
		stiffness: 20,
		restDelta: 2
	  }
	}),
	closed: {
	  clipPath: "circle(30px at 80px 80px)",
	  transition: {
		delay: 0.5,
		type: "spring",
		stiffness: 400,
		damping: 40
	  }
	}
};

function Header({ theme }: { theme: Theme | null }) {
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [stickyHeader, setStickyHeader] = useState(false);
	return (
		<header className="absolute bg-transparent text-foreground inset-x-0 top-0 z-50">
			<nav aria-label="Global" className="container flex items-center justify-between px-6 lg:px-8">
				<div className="flex lg:flex-1">
					<Logo />
				</div>
				<div className="flex lg:hidden">
					<button
					type="button"
					onClick={() => setMobileMenuOpen(true)}
					className={cn(
						"-m-2.5 inline-flex items-center justify-center rounded-md p-2.5",
						mobileMenuOpen && "hidden"
					)}
					>
						<span className="sr-only">Open main menu</span>
						<Bars3Icon aria-hidden="true" className="size-6" />
					</button>
				</div>
				<div className="hidden lg:flex lg:gap-x-12">
					{navigation.map((item) => (
					<HashNavLink key={item.name} to={item.href} className="text-lg tracking-wide font-semibold">
						{item.name}
					</HashNavLink>
					))}
				</div>
				<div className="hidden lg:flex lg:flex-1 lg:justify-end lg:gap-3">
					<Link className={cn("tracking-wide", buttonVariants({ variant: "default" }))} to="tel:9373010302">
						(937)301-0302
					</Link>
					<ThemeSwitch userPreference={theme} />
				</div>
			</nav>
			<AnimatePresence>
				{mobileMenuOpen && (
					<Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
						<div className="fixed inset-0 z-10" />
						<motion.div
							className="fixed inset-0 z-10 bg-background"
							variants={mobile}
							initial="closed"
							animate={mobileMenuOpen ? "open" : "closed"}
						/>
						<DialogPanel 
						as={motion.div}
						initial={"closed"}
						animate={mobileMenuOpen ? "open" : "closed"}
						className="fixed inset-y-0 right-0 z-20 w-full overflow-y-auto px-6 pb-6 sm:ring-1 sm:ring-gray-900/10"
						>
							<div className="flex items-center justify-between">
								<Logo />
								<button
									type="button"
									onClick={() => setMobileMenuOpen(false)}
									className="-m-2.5 rounded-md p-2.5 text-foreground"
								>
									<span className="sr-only">Close menu</span>
									<svg width="23" height="23" viewBox="0 0 23 23">
									<Path
										variants={{
										closed: { d: "M 2 2.5 L 20 2.5" },
										open: { d: "M 3 16.5 L 17 2.5" }
										}}
									/>
									<Path
										d="M 2 9.423 L 20 9.423"
										variants={{
										closed: { opacity: 1 },
										open: { opacity: 0 }
										}}
										transition={{ duration: 0.1 }}
									/>
									<Path
										variants={{
										closed: { d: "M 2 16.346 L 20 16.346" },
										open: { d: "M 3 2.5 L 17 16.346" }
										}}
									/>
									</svg>
								</button>
							</div>
							<div className="mt-6 flow-root">
								<div className="-my-6 divide-y divide-gray-500/10">
									<motion.div 
									variants={{ 
										open: { 
											transition: { 
												staggerChildren: 0.07, 
												delayChildren: 0.2 
											} 
										}, 
										closed: { 
											transition: { 
												staggerChildren: 0.05, 
												staggerDirection: -1 
											} 
										} 
									}}
									className="space-y-2 py-6"
									>
									{navigation.map((item) => (
										<MotionLink
										key={item.name}
										to={item.href}
										variants={{
											open: {
											  y: 0,
											  opacity: 1,
											  transition: {
												y: { stiffness: 1000, velocity: -100 }
											  }
											},
											closed: {
											  y: 50,
											  opacity: 0,
											  transition: {
												y: { stiffness: 1000 }
											  }
											}
										}}
										className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-foreground hover:bg-muted"
										closeMenu={() => setMobileMenuOpen(false)}
										>
										{item.name}
										</MotionLink>
									))}
										<MotionLink 
										className={buttonVariants({ variant: "default" })} 
										to="tel:9373010302"
										variants={{
											open: {
											  y: 0,
											  opacity: 1,
											  transition: {
												y: { stiffness: 1000, velocity: -100 }
											  }
											},
											closed: {
											  y: 50,
											  opacity: 0,
											  transition: {
												y: { stiffness: 1000 }
											  }
											}
										}}
										>
											(937)301-0302
										</MotionLink>
									</motion.div>
								</div>
							</div>
						</DialogPanel>
					</Dialog>
				)}
			</AnimatePresence>
		</header>
	)
}

function Path(props: any) {
	return (
		<motion.path
		fill="transparent"
		strokeWidth="3"
		stroke="currentColor"
		strokeLinecap="round"
		{...props}
	  />
	)
}

function Footer() {
	return (
		<footer className='bg-accent text-accent-foreground'>
			<div className="mx-auto max-w-7xl px-6 py-16 sm:py-24 lg:px-8 lg:py-32">
				<div className="xl:grid xl:grid-cols-3 xl:gap-8">
					<div className="space-y-4">
						<Logo />
						<Link to="tel:9373010302" className="text-sm/6">
							(937)301-0302
						</Link>
						<address className="text-sm/6">
							2555 S Dixie Dr, Suite 208 Dayton, OH 45409
						</address>
						<div className="flex gap-x-6 mt-6">
						{social.map((item) => (
							<a key={item.name} href={item.href}>
							<span className="sr-only">{item.name}</span>
							<item.icon aria-hidden="true" className="size-6" />
							</a>
						))}
						</div>
					</div>
					<div className="mt-16 flex flex-col md:flex-row md:flex-wrap gap-x-12 gap-y-3 xl:col-span-2 xl:mt-0">
					{navigation.map((item) => (
						<HashNavLink key={item.name} to={item.href}>
						{item.name}
						</HashNavLink>
					))}
					</div>
				</div>
			</div>
		</footer>
	)
}

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
